import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw, Package, MapPin, Trash2, Radar, Copy, X, Loader2, ChevronRight, Send, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SectionLabel from "@/components/SectionLabel";

const STATUS_OPTIONS = [
  "Pending Pickup",
  "Package Collected",
  "In Transit",
  "Border Crossing",
  "Held by Customs",
  "Released by Customs",
  "Customs Clearance",
  "Out for Delivery",
  "Delivered",
  "Vaulted",
  "On Hold",
  "Delayed",
  "Damaged",
  "Returned to Sender",
];
const CARGO_TYPES = ["Cash in Transit", "Gold & Bullion", "Fine Art", "High-Tech Components", "Offshore Cargo", "Other"];
const SEC_LEVELS = ["Standard", "Enhanced", "Maximum", "Sovereign"];

export default function Dashboard() {
  const { user } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab] = useState("shipments");

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, qRes] = await Promise.all([
        api.get("/shipments"),
        api.get("/quotes"),
      ]);
      setShipments(sRes.data);
      setQuotes(qRes.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCreated = (s) => {
    setShipments([s, ...shipments]);
    setShowCreate(false);
    setSelected(s);
    toast.success(`Shipment created · ${s.tracking_number}`);
  };

  const onUpdated = (s) => {
    setShipments(shipments.map(x => x.id === s.id ? s : x));
    setSelected(s);
    toast.success("Status updated · client notified if email on file");
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this shipment? This cannot be undone.")) return;
    try {
      await api.delete(`/shipments/${id}`);
      setShipments(shipments.filter(x => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Shipment removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const counts = {
    active: shipments.filter(s => !["Delivered", "Vaulted", "Returned to Sender"].includes(s.status)).length,
    delivered: shipments.filter(s => ["Delivered", "Vaulted"].includes(s.status)).length,
    hold: shipments.filter(s => ["On Hold", "Held by Customs", "Delayed", "Damaged"].includes(s.status)).length,
  };

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-12">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-6 mb-10">
        <div>
          <SectionLabel>Control Room · Tier IV</SectionLabel>
          <h1 className="mt-4 font-display text-4xl lg:text-5xl font-black text-[#F0F4F8]">
            Operations <span className="text-[#D4AF37]">Command</span>
          </h1>
          <div className="mt-2 font-mono text-xs uppercase tracking-[0.25em] text-[#9EABC0]">
            Signed in · {user?.email}
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={load}
            data-testid="dash-refresh"
            className="border border-[#A1AEC2]/30 text-[#F0F4F8] hover:border-[#D4AF37] font-mono text-xs uppercase tracking-widest px-5 py-3 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="dash-create"
            className="bg-[#D4AF37] text-[#060C1F] hover:bg-[#C5A059] font-mono text-xs uppercase tracking-widest px-5 py-3 flex items-center gap-2 font-bold"
          >
            <Plus className="w-4 h-4" /> New Shipment
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#A1AEC2]/15 border border-[#A1AEC2]/15 mb-10">
        <Stat label="Total Shipments" value={shipments.length} />
        <Stat label="Active" value={counts.active} accent />
        <Stat label="Delivered / Vaulted" value={counts.delivered} />
        <Stat label="Held / Delayed" value={counts.hold} warn={counts.hold > 0} />
      </div>

      {/* Tabs */}
      <div className="flex gap-8 border-b border-[#A1AEC2]/15 mb-6">
        <TabButton active={tab === "shipments"} onClick={() => setTab("shipments")} testid="tab-shipments">
          Shipments <span className="ml-2 text-[#D4AF37]">{shipments.length}</span>
        </TabButton>
        <TabButton active={tab === "quotes"} onClick={() => setTab("quotes")} testid="tab-quotes">
          Quote Requests <span className="ml-2 text-[#D4AF37]">{quotes.length}</span>
        </TabButton>
      </div>

      {tab === "shipments" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Shipment list */}
          <div className="lg:col-span-5 space-y-2 max-h-[720px] overflow-auto pr-2" data-testid="shipment-list">
            {loading && shipments.length === 0 && (
              <div className="text-center py-16 text-[#9EABC0]">
                <Loader2 className="w-6 h-6 mx-auto animate-spin text-[#D4AF37]" />
                <div className="mt-4 font-mono text-xs uppercase tracking-widest">Loading manifests…</div>
              </div>
            )}
            {!loading && shipments.length === 0 && (
              <div className="border border-dashed border-[#A1AEC2]/25 p-10 text-center">
                <Package className="w-8 h-8 mx-auto text-[#9EABC0]" />
                <div className="mt-4 text-[#F0F4F8]">No shipments yet</div>
                <div className="mt-2 text-sm text-[#9EABC0]">Create your first shipment to generate a tracking number.</div>
              </div>
            )}
            {shipments.map(s => (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                data-testid={`shipment-item-${s.id}`}
                className={`w-full text-left p-4 border transition-all ${
                  selected?.id === s.id ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#A1AEC2]/20 hover:border-[#D4AF37]/50 bg-[#0A1024]"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-mono text-sm text-[#F0F4F8]">{s.tracking_number}</div>
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EABC0]">
                      {s.client_name} · {s.cargo_type}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#9EABC0]" />
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="text-[#D4AF37] font-mono uppercase tracking-widest">{s.status}</span>
                  <span className="text-[#9EABC0] font-mono">{s.origin_country} → {s.destination_country}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Detail / editor */}
          <div className="lg:col-span-7">
            {selected ? (
              <ShipmentEditor
                key={selected.id}
                shipment={selected}
                onUpdated={onUpdated}
                onDelete={() => onDelete(selected.id)}
              />
            ) : (
              <div className="border border-dashed border-[#A1AEC2]/20 p-16 text-center h-full flex items-center justify-center">
                <div>
                  <Radar className="w-10 h-10 text-[#D4AF37] mx-auto" />
                  <div className="mt-6 font-display text-xl text-[#F0F4F8]">Select a shipment</div>
                  <div className="mt-2 text-sm text-[#9EABC0]">Choose from the list to view its timeline and update status.</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "quotes" && (
        <div className="border border-[#A1AEC2]/20 bg-[#0A1024]" data-testid="quotes-list">
          {quotes.length === 0 && (
            <div className="p-16 text-center text-[#9EABC0]">No quote requests yet.</div>
          )}
          {quotes.map(q => (
            <div key={q.id} className="p-6 border-b border-[#A1AEC2]/10 last:border-0 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <div className="font-display font-semibold text-[#F0F4F8]">{q.company_name}</div>
                <div className="font-mono text-xs text-[#9EABC0] mt-1">{q.contact_name} · {q.email}</div>
                {q.message && <div className="mt-2 text-sm text-[#9EABC0]">{q.message}</div>}
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EABC0]">Route</div>
                <div className="text-[#F0F4F8] mt-1">{q.origin_country} → {q.destination_country}</div>
                <div className="font-mono text-xs text-[#D4AF37] mt-1">{q.cargo_type}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EABC0]">Security</div>
                <div className="text-[#D4AF37] font-semibold mt-1">{q.security_level}</div>
                {q.estimated_value && <div className="font-mono text-xs text-[#F0F4F8] mt-1">{q.estimated_value}</div>}
                <div className="font-mono text-[10px] text-[#9EABC0] mt-2">{new Date(q.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={onCreated} />}
    </div>
  );
}

function Stat({ label, value, accent, warn }) {
  return (
    <div className="bg-[#060C1F] p-6">
      <div className={`font-display text-3xl lg:text-4xl font-black ${warn ? "text-red-400" : accent ? "text-[#D4AF37]" : "text-[#F0F4F8]"}`}>{value}</div>
      <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EABC0]">{label}</div>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`pb-4 font-mono text-xs uppercase tracking-[0.25em] border-b-2 ${active ? "border-[#D4AF37] text-[#F0F4F8]" : "border-transparent text-[#9EABC0] hover:text-[#F0F4F8]"}`}
    >
      {children}
    </button>
  );
}

function ShipmentEditor({ shipment, onUpdated, onDelete }) {
  const [form, setForm] = useState({
    status: shipment.status,
    current_country: shipment.current_country,
    current_location: shipment.current_location || "",
    notes: "",
    notify_client: true,
  });
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/shipments/${shipment.id}/status`, form);
      onUpdated(data);
      setForm({ ...form, notes: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(shipment.tracking_number);
    toast.success("Tracking number copied");
  };

  const resendEmail = async () => {
    setResending(true);
    try {
      const { data } = await api.post(`/shipments/${shipment.id}/test-email`);
      toast.success(`Email dispatched to ${data.sent_to}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Email failed");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="border border-[#A1AEC2]/20 bg-[#0A1024]">
      <div className="p-6 border-b border-[#A1AEC2]/15 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Tracking Number</div>
          <div className="mt-2 flex items-center gap-3">
            <span className="font-mono text-2xl text-[#F0F4F8]">{shipment.tracking_number}</span>
            <button onClick={copy} className="text-[#9EABC0] hover:text-[#D4AF37]" data-testid="copy-tn"><Copy className="w-4 h-4" /></button>
          </div>
          <div className="mt-2 text-sm text-[#9EABC0]">
            {shipment.client_name} · {shipment.cargo_type} · {shipment.security_level}
          </div>
          {shipment.client_email && (
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] flex items-center gap-2">
              <Mail className="w-3 h-3" /> {shipment.client_email}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {shipment.client_email && (
            <button
              onClick={resendEmail}
              disabled={resending}
              data-testid="resend-email"
              className="border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 font-mono text-xs uppercase tracking-widest px-4 py-2 flex items-center gap-2 disabled:opacity-60"
            >
              {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Resend Email
            </button>
          )}
          <button
            onClick={onDelete}
            data-testid="delete-shipment"
            className="border border-red-800/60 text-red-400 hover:bg-red-900/20 font-mono text-xs uppercase tracking-widest px-4 py-2 flex items-center gap-2"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>

      <form onSubmit={save} className="p-6 border-b border-[#A1AEC2]/15 space-y-5" data-testid="update-form">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Log Movement Update</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EABC0]">Status</label>
            <select data-testid="upd-status" value={form.status} onChange={e => upd("status", e.target.value)} className={inputCls}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EABC0]">Current Country</label>
            <input required data-testid="upd-country" value={form.current_country} onChange={e => upd("current_country", e.target.value)} className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EABC0]">Current Location</label>
            <input data-testid="upd-location" value={form.current_location} onChange={e => upd("current_location", e.target.value)} placeholder="e.g. Zurich Airport Cargo Terminal 3" className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EABC0]">Notes</label>
            <textarea data-testid="upd-notes" rows={2} value={form.notes} onChange={e => upd("notes", e.target.value)} placeholder="Optional handoff details, seal numbers, customs reference, etc." className={`${inputCls} resize-none`} />
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            <input id="notify" type="checkbox" checked={form.notify_client} onChange={e => upd("notify_client", e.target.checked)} data-testid="upd-notify" className="w-4 h-4 accent-[#D4AF37]" />
            <label htmlFor="notify" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EABC0] cursor-pointer">
              Send encrypted email notification to client {shipment.client_email ? `(${shipment.client_email})` : "(no email on file)"}
            </label>
          </div>
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="upd-submit"
          className="bg-[#D4AF37] text-[#060C1F] font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#C5A059] disabled:opacity-60 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
          Log Update
        </button>
      </form>

      <div className="p-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Event Timeline</div>
        <div className="space-y-3 max-h-[400px] overflow-auto">
          {[...(shipment.events || [])].reverse().map((ev, i) => (
            <div key={i} className={`border-l-2 ${i === 0 ? "border-[#D4AF37]" : "border-[#A1AEC2]/30"} pl-4 py-1`}>
              <div className="flex flex-wrap justify-between gap-2">
                <span className={`font-display font-semibold text-sm ${i === 0 ? "text-[#D4AF37]" : "text-[#F0F4F8]"}`}>{ev.status}</span>
                <span className="font-mono text-[10px] text-[#9EABC0]">{new Date(ev.timestamp).toLocaleString()}</span>
              </div>
              <div className="text-xs text-[#9EABC0] mt-1">{ev.country}{ev.location ? ` · ${ev.location}` : ""}</div>
              {ev.notes && <div className="text-xs text-[#F0F4F8] mt-1">{ev.notes}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    client_name: "", client_email: "", client_phone: "", cargo_type: "Cash in Transit",
    origin_country: "", destination_country: "",
    security_level: "Enhanced", estimated_value: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // strip empty optional fields
      const payload = { ...form };
      if (!payload.client_email) delete payload.client_email;
      if (!payload.client_phone) delete payload.client_phone;
      const { data } = await api.post("/shipments", payload);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#060C1F]/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={e => e.stopPropagation()}
        onSubmit={submit}
        data-testid="create-form"
        className="w-full max-w-2xl bg-[#0A1024] border border-[#D4AF37]/40 max-h-[90vh] overflow-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-[#A1AEC2]/15">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">New Shipment</div>
            <h3 className="mt-1 font-display text-2xl font-bold text-[#F0F4F8]">Register Consignment</h3>
          </div>
          <button type="button" onClick={onClose} className="text-[#9EABC0] hover:text-[#F0F4F8]" data-testid="close-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Client / Company" required>
            <input required data-testid="cf-client" value={form.client_name} onChange={e => upd("client_name", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Cargo Type">
            <select data-testid="cf-cargo" value={form.cargo_type} onChange={e => upd("cargo_type", e.target.value)} className={inputCls}>
              {CARGO_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Client Email (for notifications)">
            <input type="email" data-testid="cf-client-email" value={form.client_email} onChange={e => upd("client_email", e.target.value)} placeholder="client@company.com" className={inputCls} />
          </Field>
          <Field label="Client Phone">
            <input data-testid="cf-client-phone" value={form.client_phone} onChange={e => upd("client_phone", e.target.value)} placeholder="+1 555 …" className={inputCls} />
          </Field>
          <Field label="Origin Country" required>
            <input required data-testid="cf-origin" value={form.origin_country} onChange={e => upd("origin_country", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Destination Country" required>
            <input required data-testid="cf-dest" value={form.destination_country} onChange={e => upd("destination_country", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Security Level">
            <select data-testid="cf-sec" value={form.security_level} onChange={e => upd("security_level", e.target.value)} className={inputCls}>
              {SEC_LEVELS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Estimated Value">
            <input data-testid="cf-value" value={form.estimated_value} onChange={e => upd("estimated_value", e.target.value)} placeholder="$1M USD" className={inputCls} />
          </Field>
          <div className="md:col-span-2">
            <Field label="Notes">
              <textarea data-testid="cf-notes" rows={3} value={form.notes} onChange={e => upd("notes", e.target.value)} className={`${inputCls} resize-none`} />
            </Field>
          </div>
        </div>
        <div className="p-6 border-t border-[#A1AEC2]/15 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="font-mono text-xs uppercase tracking-widest px-6 py-3 text-[#9EABC0] hover:text-[#F0F4F8]">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="cf-submit"
            className="bg-[#D4AF37] text-[#060C1F] font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#C5A059] disabled:opacity-60 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Generate Tracking
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls = "mt-2 w-full bg-[#060C1F] border border-[#A1AEC2]/30 text-[#F0F4F8] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] px-4 py-3 outline-none text-sm";

function Field({ label, required, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EABC0]">
        {label}{required && <span className="text-[#D4AF37] ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
